import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { FetchParams, InviteStepFetchInterceptorParams, InterceptPaths } from './types';
import { useInviteStepTaskPolling } from './use-invite-step-create-project-task-polling-context';
import { getInviteeProjectRoleId, requestHandlers } from './utils';

export const useInviteStepFetchInterceptor = ({
	cloudId,
	isEnabled = true,
}: InviteStepFetchInterceptorParams) => {
	const originalFetch = useRef(global?.fetch); // DO NOT MODIFY
	const fetchRef = originalFetch.current;

	const { projectId, projectRoleIds, jiraEdition } = useInviteStepTaskPolling();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const preferAdminRole = jiraEdition === 'free'; // Free edition requires users to be invited as Admins
	const inviteeRoleId = useMemo(
		() => getInviteeProjectRoleId(projectRoleIds, preferAdminRole),
		[preferAdminRole, projectRoleIds],
	);

	const getInviteStepInterceptor = useCallback(
		({ cloudId }: InviteStepFetchInterceptorParams) =>
			async (...fetched: FetchParams) => {
				const [resource] = fetched;
				const fetchedString = resource.toString();
				const isIntercepted = InterceptPaths.some((path) => fetchedString.includes(path));
				const isRelativePath = !fetchedString.startsWith('http');

				if (isIntercepted && isRelativePath) {
					const targetPath = InterceptPaths.find((path) => fetchedString.includes(path));

					if (targetPath !== undefined && fetchRef) {
						const handler = requestHandlers[targetPath];

						try {
							const onComplete = async (args: object) => {
								await Promise.resolve(
									createAnalyticsEvent({
										action: 'succeeded',
										actionSubject: 'inviteStepFetchInterceptor',
										eventType: 'track',
										attributes: {
											...args,
											path: targetPath,
											projectId,
											inviteeRoleId,
											jiraEdition,
										},
									}).fire(),
								);
							};

							const onError = async (args: object) => {
								createAnalyticsEvent({
									action: 'failed',
									actionSubject: 'onboardingWizardInviteStepInviteRequest',
									eventType: 'operational',
									attributes: {
										...args,
										path: targetPath,
										projectId,
										inviteeRoleId,
										jiraEdition,
									},
								}).fire();
							};

							return handler(fetchRef, fetched, {
								cloudId,
								projectId,
								inviteeRoleId,
								onComplete,
								onError,
							});
						} catch (error) {
							// fail silently and fire analytics
							createAnalyticsEvent({
								action: 'failed',
								actionSubject: 'inviteStepFetchInterceptor',
								eventType: 'track',
								attributes: {
									error: (error as Error)?.message,
									path: targetPath,
									projectId,
									inviteeRoleId,
									jiraEdition,
								},
							}).fire();
						}
					}
				}

				// call original fetch as a default
				return fetchRef?.(...fetched);
			},
		[createAnalyticsEvent, fetchRef, inviteeRoleId, projectId],
	);

	// Set the interceptor
	useEffect(() => {
		if (!isEnabled || !fetchRef) {
			// If in a non-browser environment, do nothing
			return;
		}

		Object.assign(global, {
			fetch: getInviteStepInterceptor({ cloudId }),
		});

		return () => {
			// Restore original fetch on unmount
			Object.assign(global, {
				fetch: fetchRef,
			});
		};
	}, [cloudId, fetchRef, getInviteStepInterceptor, isEnabled]);
};
